<template>
  <div class="my-3 px-4 d-flex flex-column" data-testid="document-page">
    <div class="page-divider">
      <div
        class="page-number i-text-subheading-3"
        :class="{ 'on-dark': onDark }"
      >
        Page {{ pageIndex + 1 }} / {{ document.total_pages }}
      </div>
    </div>
    <div
      ref="documentPage"
      :class="[
        'mx-auto document-page fs-exclude',
        {
          card: document.file_type !== fileTypes.IMAGE,
        },
      ]"
      @mousedown="startDrag"
      @mousemove="mouseMove"
      @mouseup="endDrag"
    >
      <pdf
        v-if="document.file_type !== fileTypes.IMAGE"
        :id="pageIndex"
        :src="document.urls.original_file"
        :page="pageIndex + 1"
        :scale="zoomFactor"
        :rotate="rotation"
        data-testid="document-pdf-page"
        @pagerendered="overlayVisible = true"
      >
      </pdf>
      <div v-else class="rotation-wrapper-outer">
        <div class="rotation-wrapper-inner">
          <img
            ref="pageImage"
            :src="document.urls.original_file"
            :style="{ width: `${zoomFactor * 133}%` }"
            :class="['document-img', `rotate-${rotation}`]"
            crossorigin="anonymous"
            data-testid="document-image-page"
            @load="onImgLoad"
          />
        </div>
      </div>
      <div v-if="overlayVisible">
        <div v-if="showPageCheckSignals">
          <requirement-signal-overlay
            v-for="(signal, index) in pageCheckSignals"
            :key="`${index}, ${signal.instance.boundingBox.top}, ${signal.instance.boundingBox.x0}`"
            :document="document"
            :signal="signal"
            :document-page="$refs.pageImage || $refs.documentPage"
            :rotation="rotation"
            :file-type="document.file_type"
            :image-position-multiplier="getImageMultiplier"
            :zoom-factor="zoomFactor"
          />
        </div>
        <fraud-signal-overlay
          v-for="(signal, idx) in fraudSignals"
          :key="idx"
          :document="document"
          :signal="signal"
          :document-page="$refs.pageImage || $refs.documentPage"
          :rotation="rotation"
          :image-position-multiplier="getImageMultiplier"
          :zoom-factor="zoomFactor"
        >
        </fraud-signal-overlay>
        <overlay-container
          v-for="overlay in genericOverlays"
          :key="`${overlay.instance.boundingBox.top}, ${overlay.instance.boundingBox.x0}`"
          :overlay="overlay"
          :document-page="$refs.pageImage || $refs.documentPage"
          :rotation="rotation"
          :file-type="document.file_type"
          :image-position-multiplier="getImageMultiplier"
          :zoom-factor="zoomFactor"
        />
      </div>

      <div
        v-if="hasDragCoordinates"
        class="selection-box"
        data-testid="selection-box"
        :style="dragBoundaryStyle"
      />
      <div
        v-if="calculationsEnabled && !hasDragCoordinates && mouseEndX"
        class="document-calculator-mouse-follow-box px-2 py-1"
        data-testid="mouse-follow-box"
        :style="{
          left: mouseBoxPosition.left,
          top: mouseBoxPosition.top,
        }"
      >
        <p>Click and drag over text to calculate the sum of the numbers</p>
      </div>
      <div
        v-if="hasDragCoordinates && !dragging && selectedNumbers.length > 0"
        class="selected-numbers"
        data-testid="selected-numbers"
        :style="selectedNumbersBoxPosition"
      >
        <div
          v-for="selectedNumber in selectedNumbers"
          :key="selectedNumber.dx + selectedNumber.dy"
          :style="{
            right: `${selectedNumber.rightDx + 12}px`,
            top: `${selectedNumber.top - selectedNumbers[0].top + 16}px`,
            fontSize: `${selectedNumber.fontSize}px`,
            lineHeight: `${selectedNumber.fontSize}px`,
          }"
          class="selected-number-item"
        >
          {{
            selectedNumber.value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })
          }}
        </div>
        <div
          :style="{
            right: '12px',
            top: `${
              selectedNumbers[selectedNumbers.length - 1].top +
              -selectedNumbers[0].top +
              selectedNumbers[selectedNumbers.length - 1].height +
              20
            }px`,
            fontSize: `${sumTotalFontSize}px`,
            lineHeight: `${sumTotalFontSize}px`,
          }"
          class="selected-number-item sum"
          data-testid="selected-numbers-total"
        >
          <strong
            ><span>{{
              selectedNumbersSum.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })
            }}</span></strong
          >
        </div>
      </div>
    </div>
    <i-toast></i-toast>
  </div>
</template>

<script>
import * as fileTypes from '../../constants/fileTypes';
import requirementConfig from '../../constants/requirementConfig';
import OverlayContainer from './overlays/OverlayContainer';
import FraudSignalOverlay from './overlays/FraudSignalOverlay';
import RequirementSignalOverlay from './overlays/RequirementSignalOverlay';
import PdfViewer from '@/V2/documents/PdfViewer';
import { iToast } from '@inscribe/signal';

const MAX_FONT_SIZE = 35;
const SELECTED_NUMBERS_PADDING = 16;

export default {
  components: {
    iToast,
    pdf: PdfViewer,
    'overlay-container': OverlayContainer,
    'fraud-signal-overlay': FraudSignalOverlay,
    'requirement-signal-overlay': RequirementSignalOverlay,
  },
  props: {
    calculationsEnabled: {
      type: Boolean,
      required: true,
    },
    document: {
      type: Object,
      required: true,
    },
    pageIndex: {
      type: Number,
      required: true,
    },
    pageOverlays: {
      type: Array,
      default: () => [],
    },
    rotation: {
      type: Number,
      required: true,
    },
    zoomFactor: {
      type: Number,
      required: true,
    },
    showPageCheckSignals: {
      type: Boolean,
      default: true,
    },
    onDark: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      fileTypes,
      mounted: false,
      dragging: false,
      mouseStartX: undefined,
      mouseStartY: undefined,
      mouseEndX: undefined,
      mouseEndY: undefined,
      selectedNumbers: [],
      selectedNumbersBbox: undefined,
      imagesLoaded: 0,
      overlayVisible: false,
    };
  },
  computed: {
    getImageMultiplier() {
      const img = this.$refs.pageImage;
      const baseX =
        this.mounted && img ? img.clientWidth / img.naturalWidth : 0;
      const baseY =
        this.mounted && img ? img.clientHeight / img.naturalHeight : 0;
      const isNormallyOrientated = this.rotation % 180 === 0;
      return this.imagesLoaded
        ? {
            x: isNormallyOrientated ? baseX : baseY,
            y: isNormallyOrientated ? baseY : baseX,
          }
        : { x: 0, y: 0 };
    },
    hasDragCoordinates() {
      return (
        this.calculationsEnabled &&
        this.mouseStartX &&
        this.mouseStartY &&
        this.mouseEndX &&
        this.mouseEndY
      );
    },
    dragBoundaryStyle() {
      return this.bboxToPositions(this.selectionPosition);
    },
    selectedNumbersBoxPosition() {
      return this.bboxToPositions(this.selectedNumbersBbox);
    },
    mouseBoxPosition() {
      return this.bboxToPositions({
        left: this.mouseEndX + SELECTED_NUMBERS_PADDING,
        top: this.mouseEndY,
      });
    },
    fraudSignals() {
      return this.pageOverlays.filter((overlay) => {
        return overlay.instance.boundingBox && overlay.signalName;
      });
    },
    genericOverlays() {
      return this.pageOverlays.filter((overlay) => {
        return overlay.instance.boundingBox && !overlay.signalName;
      });
    },
    pageCheckSignals() {
      const checkSignals = [];
      this.document.checks.forEach(({ requirements }) => {
        requirements
          .filter(
            ({ actual_value, name }) =>
              requirementConfig[name] &&
              requirementConfig[name].hasSignals &&
              actual_value &&
              Object.keys(actual_value).length > 0 &&
              actual_value[this.pageIndex + 1],
          )
          .forEach((requirement) => {
            requirement.actual_value[this.pageIndex + 1].forEach((signal) => {
              const config = requirementConfig[requirement.name];
              checkSignals.push(
                config.transformSignal(signal, requirement, config),
              );
            });
          });
      });
      return checkSignals;
    },
    selectionPosition() {
      return {
        top: Math.min(this.mouseStartY, this.mouseEndY),
        left: Math.min(this.mouseStartX, this.mouseEndX),
        width: Math.abs(this.mouseStartX - this.mouseEndX),
        height: Math.abs(this.mouseStartY - this.mouseEndY),
      };
    },
    selectedNumbersSum() {
      return this.selectedNumbers.reduce(
        (sumAcc, number) => sumAcc + number.value,
        0,
      );
    },
  },
  watch: {
    zoomFactor() {
      if (this.hasDragCoordinates) {
        this.mouseStartX = undefined;
        this.mouseStartY = undefined;
        this.mouseEndX = undefined;
        this.mouseEndY = undefined;
        this.selectedNumbers = [];
        this.selectedNumbersBbox = undefined;
        this.sumTotalFontSize = undefined;
      }
    },
  },
  mounted() {
    setTimeout(() => {
      this.mounted = true;
    }, 500);
  },
  methods: {
    onImgLoad() {
      this.overlayVisible = true;
      this.imagesLoaded++;
    },
    bboxToPositions(bbox) {
      const { top, left, width, height } = bbox;
      return {
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`,
        height: `${height}px`,
      };
    },
    formatTextNodeText(nodeText) {
      const text = nodeText.replace(/[^0-9,.-]/g, '');
      const decimalSeparator = /[\d.]+(,\d\d)$/.test(text);
      const formattedText = decimalSeparator
        ? text.replace(/./g, '').replace(/,/g, '.')
        : text.replace(/,/g, '');
      return formattedText;
    },
    calculateSelectionNumbersBoundingBox(sortedNumbers) {
      const { left: selectionLeft, width: selectionWidth } =
        this.selectionPosition;

      const firstNumber = sortedNumbers[0];
      const lastNumber = sortedNumbers[sortedNumbers.length - 1];
      const maxX = Math.max(
        ...sortedNumbers.map(({ left, width }) => left + width),
      );
      const bboxWidth =
        maxX -
        Math.min(...sortedNumbers.map(({ left }) => left)) +
        SELECTED_NUMBERS_PADDING * 4;
      const averageFontSize =
        sortedNumbers.reduce(
          (fontSizeSum, { fontSize }) => fontSize + fontSizeSum,
          0,
        ) / sortedNumbers.length;
      this.selectedNumbersBbox = {
        top: firstNumber.top - SELECTED_NUMBERS_PADDING,
        left: selectionLeft + selectionWidth + 1,
        width: bboxWidth + 8,
        height:
          lastNumber.top -
          firstNumber.top +
          lastNumber.height +
          SELECTED_NUMBERS_PADDING * 3 +
          averageFontSize,
      };

      this.selectedNumbers = sortedNumbers.map((number) => ({
        ...number,
        rightDx: maxX - (number.left + number.width),
        fontSize: averageFontSize,
      }));
      this.sumTotalFontSize = averageFontSize;
    },
    getTextNodes(pageRect) {
      if (this.document.ocrPages && this.document.ocrPages[this.pageIndex]) {
        const image = this.$refs.documentPage.querySelector('img');
        const rect =
          this.document.file_type === fileTypes.IMAGE
            ? image.getBoundingClientRect()
            : pageRect;
        const pageHeight = rect.height;
        const pageWidth = rect.width;
        return this.document.ocrPages[this.pageIndex].words.map((wordNode) => {
          const left = pageWidth * wordNode.left;
          const right = pageWidth * wordNode.right;
          const top = pageHeight * wordNode.top;
          const bottom = pageHeight * wordNode.bottom;
          return {
            left,
            top,
            width: right - left,
            height: bottom - top,
            right,
            bottom,
            text: wordNode.text,
            fontSize: 12 * this.zoomFactor,
          };
        });
      } else {
        return [
          ...this.$refs.documentPage.querySelectorAll('.textLayer span'),
        ].map((textNode) => {
          const style = textNode.getBoundingClientRect();
          const left = style.left - pageRect.left;
          const top = style.top - pageRect.top;
          const width = style.width;
          const height = style.height;
          return {
            left,
            top,
            width,
            height,
            right: left + width,
            bottom: top + height,
            text: textNode.textContent,
            fontSize: parseFloat(textNode.style.fontSize.replace('px', '')),
          };
        });
      }
    },
    findWordsInSelection() {
      const rect = this.$refs.documentPage.getBoundingClientRect();

      const {
        top: selectionTop,
        left: selectionLeft,
        width: selectionWidth,
        height: selectionHeight,
      } = this.selectionPosition;
      const selectedNumbers = [];
      this.getTextNodes(rect).forEach((textNode) => {
        if (
          textNode.left > selectionLeft &&
          textNode.left < selectionLeft + selectionWidth &&
          textNode.right < selectionLeft + selectionWidth &&
          textNode.width < selectionWidth &&
          textNode.top > selectionTop &&
          textNode.top < selectionTop + selectionHeight &&
          textNode.height < selectionHeight
        ) {
          const formattedText = this.formatTextNodeText(textNode.text);

          if (formattedText && !isNaN(formattedText)) {
            selectedNumbers.push({
              ...textNode,
              value: parseFloat(formattedText),
              fontSize: Math.min(MAX_FONT_SIZE, textNode.fontSize),
              dy: textNode.top - selectionTop,
            });
          }
        }
      });
      const sortedNumbers = selectedNumbers.sort(
        (numA, numB) => numA.dy - numB.dy,
      );
      if (sortedNumbers.length > 0) {
        this.calculateSelectionNumbersBoundingBox(sortedNumbers);
      } else {
        this.selectedNumbers = sortedNumbers;
        this.selectedNumbersBbox = undefined;
        this.sumTotalFontSize = undefined;
      }
    },
    startDrag(event) {
      if (
        this.calculationsEnabled &&
        (
          this.$refs.documentPage.querySelector('.textLayer') ||
          this.$refs.documentPage.querySelector('img')
        ).contains(event.target)
      ) {
        this.dragging = true;
        const rect = this.$refs.documentPage.getBoundingClientRect();
        this.mouseStartX = event.clientX - rect.left;
        this.mouseStartY = event.clientY - rect.top;
        this.mouseEndX = undefined;
        this.mouseEndY = undefined;
        this.selectedNumbers = [];
        this.selectedNumbersBbox = undefined;
      }
    },
    endDrag() {
      this.dragging = false;
      this.findWordsInSelection();
    },
    mouseMove(event) {
      if (this.calculationsEnabled && !this.selectedNumbersBbox) {
        const rect = this.$refs.documentPage.getBoundingClientRect();
        this.mouseEndX = event.clientX - rect.left;
        this.mouseEndY = event.clientY - rect.top;

        if (event.buttons !== 1) {
          this.mouseStartX = undefined;
          this.mouseStartY = undefined;
        }
      }
    },
  },
};
</script>

<style lang="scss">
.page-divider {
  display: flex;
  justify-content: center;
  margin-bottom: 14px;

  .page-number {
    background: $light-neutral-10;
    color: $light-neutral-110;
    border-radius: 30px;
    padding: 2px 8px;

    &.on-dark {
      background: $dark-neutral-10;
      color: $dark-neutral-110;
    }
  }
}
.document-page {
  .document-calculator-mouse-follow-box {
    background: $color-navy-blue-80;
    color: $color-white;
    font-size: $font-size-small;
    border-radius: $border-radius-default;
    max-width: 200px;
    min-width: 200px;
    position: absolute;
  }

  .selection-box {
    background: rgba(137, 223, 252, 0.2); // $color-sky-blue-60;
    border-radius: $border-radius-default;
    display: block;
    border: 2px solid $color-sky-blue-60;
    position: absolute;
  }

  .selected-numbers {
    background: $color-navy-blue-80;
    border-radius: $border-radius-default;
    display: block;
    position: absolute;
    font-family: monospace;

    .selected-number-item {
      color: $color-white;
      font-size: $font-size-small;
      position: absolute;

      &.sum {
        border-top: 2px solid $color-navy-blue-10;
        padding-top: 4px;
      }
    }
  }
}
</style>
